'use client'

import React, { useEffect, useRef, useMemo } from 'react'
import { IChartApi, ISeriesApi, UTCTimestamp, LineStyle, LineSeries } from 'lightweight-charts'
import type { 
  TrianglePattern, 
  PatternPoint, 
  Point,
  PATTERN_COLORS
} from '../../types/drawing'
import {
  calculateTriangleTrendlines,
  calculateProjectedTarget 
} from '../../types/drawing'
import { createVerticalLineData } from '../../utils/chartDataUtils'

interface TriangleProps {
  chart: IChartApi
  pattern: TrianglePattern
  onUpdate?: (pattern: TrianglePattern) => void
  onDelete?: (patternId: string) => void
}

const Triangle: React.FC<TriangleProps> = ({
  chart,
  pattern,
  onUpdate,
  onDelete
}) => {
  // Series refs for triangle lines
  const upperTrendlineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const lowerTrendlineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const convergenceLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const projectionLineRef = useRef<ISeriesApi<'Line'> | null>(null)

  // Pattern color based on type
  const patternColor = useMemo(() => {
    switch (pattern.type) {
      case 'ascending-triangle': return '#2196F3' // Blue for bullish
      case 'descending-triangle': return '#F23645' // Red for bearish
      case 'symmetrical-triangle': return '#9C27B0' // Purple for neutral
      default: return '#2962FF'
    }
  }, [pattern.type])

  // Validate pattern has enough points
  const isValidPattern = useMemo(() => {
    return pattern.patternPoints.highs.length >= 2 && pattern.patternPoints.lows.length >= 2
  }, [pattern.patternPoints])

  // Calculate trendlines and convergence
  const triangleData = useMemo(() => {
    if (!isValidPattern) return null
    
    try {
      return calculateTriangleTrendlines(
        pattern.patternPoints.highs,
        pattern.patternPoints.lows
      )
    } catch {
      return null
    }
  }, [pattern.patternPoints, isValidPattern])

  // Calculate projected target
  const projectedTarget = useMemo(() => {
    if (!pattern.confirmation || !triangleData) return null
    
    const triangleHeight = Math.abs(
      pattern.patternPoints.highs[0].price - pattern.patternPoints.lows[0].price
    )
    
    // Breakout price would be the latest trendline price
    const lastHigh = pattern.patternPoints.highs[pattern.patternPoints.highs.length - 1]
    const lastLow = pattern.patternPoints.lows[pattern.patternPoints.lows.length - 1]
    const breakoutPrice = pattern.patternPoints.breakoutPoint?.price || 
      (pattern.type === 'ascending-triangle' ? lastHigh.price : lastLow.price)
    
    return calculateProjectedTarget(
      pattern.type,
      triangleHeight,
      breakoutPrice,
      false // Triangles are continuation patterns
    )
  }, [pattern, triangleData])

  // Initialize pattern drawing
  useEffect(() => {
    if (!chart || !isValidPattern || !triangleData) return

    // Create upper trendline
    upperTrendlineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      title: `${pattern.type} - Upper`,
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create lower trendline
    lowerTrendlineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      title: `${pattern.type} - Lower`,
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create convergence line (optional, to show where lines meet)
    convergenceLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      title: 'Convergence',
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create projection line if pattern is confirmed
    if (pattern.confirmation && projectedTarget) {
      projectionLineRef.current = chart.addSeries(LineSeries, {
        color: '#FFD700', // Gold color for target
        lineWidth: 2,
        lineStyle: LineStyle.Dotted,
        title: `Target: ${projectedTarget.toFixed(2)}`,
        priceLineVisible: true,
        lastValueVisible: true
      })
    }

    return () => {
      // Cleanup on unmount
      if (upperTrendlineRef.current) {
        chart.removeSeries(upperTrendlineRef.current)
        upperTrendlineRef.current = null
      }
      if (lowerTrendlineRef.current) {
        chart.removeSeries(lowerTrendlineRef.current)
        lowerTrendlineRef.current = null
      }
      if (convergenceLineRef.current) {
        chart.removeSeries(convergenceLineRef.current)
        convergenceLineRef.current = null
      }
      if (projectionLineRef.current) {
        chart.removeSeries(projectionLineRef.current)
        projectionLineRef.current = null
      }
    }
  }, [chart, isValidPattern, triangleData, patternColor, pattern.confirmation, projectedTarget])

  // Update upper trendline data
  useEffect(() => {
    if (!upperTrendlineRef.current || !triangleData) return

    // Ensure ascending time order and no duplicates
    const rawData = [
      {
        time: triangleData.upperTrendline[0].time,
        value: triangleData.upperTrendline[0].price
      },
      {
        time: triangleData.upperTrendline[1].time,
        value: triangleData.upperTrendline[1].price
      },
      // Extend to convergence point
      {
        time: triangleData.convergencePoint.time,
        value: triangleData.convergencePoint.price
      }
    ]

    // Sort by time and ensure unique timestamps
    const sortedData = rawData.sort((a, b) => a.time - b.time)
    const upperTrendlineData = sortedData.map((point, index) => ({
      time: (point.time + index) as UTCTimestamp, // Add small offset to ensure uniqueness
      value: point.value
    }))

    upperTrendlineRef.current.setData(upperTrendlineData)
  }, [triangleData])

  // Update lower trendline data
  useEffect(() => {
    if (!lowerTrendlineRef.current || !triangleData) return

    // Ensure ascending time order and no duplicates
    const rawData = [
      {
        time: triangleData.lowerTrendline[0].time,
        value: triangleData.lowerTrendline[0].price
      },
      {
        time: triangleData.lowerTrendline[1].time,
        value: triangleData.lowerTrendline[1].price
      },
      // Extend to convergence point
      {
        time: triangleData.convergencePoint.time,
        value: triangleData.convergencePoint.price
      }
    ]

    // Sort by time and ensure unique timestamps
    const sortedData = rawData.sort((a, b) => a.time - b.time)
    const lowerTrendlineData = sortedData.map((point, index) => ({
      time: (point.time + index) as UTCTimestamp, // Add small offset to ensure uniqueness
      value: point.value
    }))

    lowerTrendlineRef.current.setData(lowerTrendlineData)
  }, [triangleData])

  // Update convergence marker
  useEffect(() => {
    if (!convergenceLineRef.current || !triangleData) return

    // Create a small vertical line at convergence point using utility function
    const convergenceData = createVerticalLineData(
      triangleData.convergencePoint.time,
      triangleData.convergencePoint.price - 10, // Bottom offset for visibility
      triangleData.convergencePoint.price + 10  // Top offset for visibility
    )

    convergenceLineRef.current.setData(convergenceData)
  }, [triangleData])

  // Update projection line
  useEffect(() => {
    if (!projectionLineRef.current || !projectedTarget || !pattern.confirmation) return

    const breakoutTime = pattern.patternPoints.breakoutPoint?.time || 
      triangleData?.convergencePoint.time || 
      Date.now() / 1000

    const projectionData = [
      {
        time: breakoutTime as UTCTimestamp,
        value: projectedTarget
      },
      {
        time: (breakoutTime + 86400 * 30) as UTCTimestamp, // 30 days ahead
        value: projectedTarget
      }
    ]

    projectionLineRef.current.setData(projectionData)
  }, [projectedTarget, pattern.confirmation, pattern.patternPoints.breakoutPoint, triangleData])

  // Pattern info display
  const patternInfo = useMemo(() => {
    const quality = isValidPattern ? 'Valid' : 'Invalid'
    const confirmation = pattern.confirmation ? 'Confirmed' : 'Pending'
    const timeToConvergence = triangleData ? 
      Math.max(0, triangleData.convergencePoint.time - Date.now() / 1000) / 86400 : 0
    
    return {
      type: pattern.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      quality,
      confirmation,
      target: projectedTarget?.toFixed(2) || 'N/A',
      convergenceInDays: timeToConvergence.toFixed(1)
    }
  }, [isValidPattern, pattern.type, pattern.confirmation, projectedTarget, triangleData])

  // Component doesn't render DOM elements, pattern is drawn using chart series
  return null
}

export default Triangle

// Helper function to create a new Triangle pattern
export const createTrianglePattern = (
  id: string,
  type: 'ascending-triangle' | 'descending-triangle' | 'symmetrical-triangle',
  highs: PatternPoint[],
  lows: PatternPoint[]
): TrianglePattern => {
  if (highs.length < 2 || lows.length < 2) {
    throw new Error('Triangle pattern requires at least 2 highs and 2 lows')
  }

  // Calculate trendlines
  const triangleData = calculateTriangleTrendlines(highs, lows)
  
  // Calculate pattern duration
  const firstPoint = Math.min(highs[0].time, lows[0].time)
  const lastPoint = Math.max(
    highs[highs.length - 1].time,
    lows[lows.length - 1].time
  )
  const duration = (lastPoint - firstPoint) / 86400 // Convert to days

  const patternColor = type === 'ascending-triangle' ? '#2196F3' :
                      type === 'descending-triangle' ? '#F23645' : '#9C27B0'

  return {
    id,
    type,
    points: [...highs, ...lows].map(p => ({ time: p.time, price: p.price })),
    style: {
      lineColor: patternColor,
      lineWidth: 2,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 10,
    patternPoints: {
      highs: highs.map(h => ({ ...h, role: 'resistance', significance: 0.8 })),
      lows: lows.map(l => ({ ...l, role: 'support', significance: 0.8 })),
      convergencePoint: triangleData.convergencePoint
    },
    upperTrendline: triangleData.upperTrendline,
    lowerTrendline: triangleData.lowerTrendline,
    confirmation: false,
    duration,
    volume: undefined
  }
} 